import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'

import { I18nContext, type I18nValue } from './context'
import { DEFAULT_LOCALE, isLocale, negotiateLocale, translate, type Locale } from './messages'

/** Language choice is a non-sensitive preference, so localStorage is acceptable. */
const STORAGE_KEY = 'tryonme.locale'

function readStoredLocale(): Locale | undefined {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    return stored !== null && isLocale(stored) ? stored : undefined
  } catch {
    // Storage can be unavailable (private mode, blocked site data).
    return undefined
  }
}

function storeLocale(locale: Locale): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale)
  } catch {
    // Preference simply won't persist.
  }
}

function initialLocale(): Locale {
  return readStoredLocale() ?? negotiateLocale(navigator.languages) ?? DEFAULT_LOCALE
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale)

  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next)
    storeLocale(next)
  }, [])

  const value = useMemo<I18nValue>(
    () => ({
      locale,
      setLocale,
      t: (key, values) => translate(locale, key, values),
    }),
    [locale, setLocale],
  )

  return <I18nContext value={value}>{children}</I18nContext>
}
